import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { setActiveTabIndex } from 'slices/ui'
import { showCPopup } from 'slices/confirmation-popup-slice'
import { FormMode } from 'helpers/form-mode-enum'
import notify from 'devextreme/ui/notify'
import i18n from 'i18next'
import _ from 'lodash'

import { parseMessages } from 'helpers/parse-dll-error'
import { Selection } from 'enums/selection'
import client from 'api'

// exported for testing
export const getInitialPageState = (type: 'page'|'subpage', tNs: string | null, tabs: any[]) => {
    if (type === 'page' && tNs === null) {
        console.error('pages-slice: pages should have tNs set to string and not null')
        return {}
    }

    return {
        type,
        parentPageName: null,

        tNs,

        tabs,
        activeTabIndex: 0,

        selectionBanner: null,
        selectionBannerLookupData: null,
        selectedItem: null,
        gridSelectedItems: [],
        selectionFilter: [],

        selection: Selection.NoSelection,

        tabProps: {},

        formMode: {},
        dirty: {},
        hasDirtyTab: false,
        hasDirtyTabMain: false,

        popups: {},

        dynamicPopup: null,
        popupData: null,
        popupTitle: '',

        moduleRights: null,

        gridFilters: {
            filterValues: {},
            filterLookupValues: {}
        },

        itemsToReload: null as any,
        itemsToRemove: null as any,

        tableNamesMap: {},

        multiforms: {},
    }
}

export const switchTab = ({ name, tabName, formMode, tabProps }: any) => {
    return (dispatch: Function, getState: Function) => {
        const state = getState()
        const { type, tabs, formMode: modes, dirty, parentPageName, hasDirtyTabMain, gridSelectedItems } = state.pagesSlice[name]
        const pageHasDirty = parentPageName ? state.pagesSlice[parentPageName].hasDirtyTabMain : false
        const parentNs = parentPageName ? state.pagesSlice[parentPageName].tNs : ''

        const nextIndex = tabs.findIndex((t: any) => t.name === tabName)

        const confirmCb = () => {
            dispatch(switchTabIndex({ name, tabIndex: nextIndex }))
            if ( formMode !== undefined ) {
                dispatch(setFormModes({ name, modes: { [tabName]: formMode } }))
            }
            if ( tabProps !== undefined ) {
                dispatch(setTabProps({ name, tabName, props: tabProps }))
            }
            if ( type === 'page' ) {
                dispatch(setActiveTabIndex(nextIndex))

                if (nextIndex === 0 && !hasDirtyTabMain) {
                    if (gridSelectedItems && gridSelectedItems.length) {
                        dispatch(selectionBannerChanged({ name, banner: gridSelectedItems[0] }))
                    }
                    // reset selection banner to show the first item of gridSelectedItems
                }
            }
        }

        const rejectCb = () => {
            dispatch(switchTabIndex({ name, tabIndex: nextIndex }))
            if ( type === 'page' ) {
                dispatch(setActiveTabIndex(nextIndex))
            }
        }

        if ( type === 'subpage' && pageHasDirty) {
            const confirmCb = () => {
                dispatch(jumpToDirty({ name: parentPageName }))
            }
            dispatch(showCPopup({
                    title: i18n.t('global:label.unsavedChanges'),
                    description: i18n.t('global:question.unsavedChangesInTab', { mainItem: i18n.t(`${parentNs}:label.mainItem`), mainItemGen: i18n.t(`${parentNs}:label.mainItemGen`) }),
                    confirmCb,
                    rejectCb,
                    confirm: i18n.t('global:button.yes'),
                    cancel: i18n.t('global:button.no'),
            }))
        } else {
            if ( dirty[tabName] === true && (formMode === FormMode.Reset || formMode !== modes[tabName]) ) {
                dispatch(showCPopup({
                    title: i18n.t('global:label.discardChanges'),
                    description: i18n.t('global:question.discardChanges'),
                    confirmCb,
                    rejectCb,
                }))
            } else {
                confirmCb()
            }
        }

    }
}

export const jumpToDirty = ({ name }: any) => {
    return (dispatch: Function, getState: Function) => {
        const state = getState()
        const { type, tabs, dirty } = state.pagesSlice[name]

        const tabName = Object.keys(dirty).find((k: string) => !k.endsWith('Subpage') && dirty[k])
        const tabIndex = tabs.findIndex((x: any) => tabName === x.name)
        if (tabIndex >= 0) {
            dispatch(switchTabIndex({ name, tabIndex }))
            if ( type === 'page' ) {
                dispatch(setActiveTabIndex(tabIndex))
            }
        }
    }
}

export const selectionChanged = ({ name, selectedItems, selectionFilter, nextAction, cancelAction, force=false }: any) => {
    // todo: reset dirty after changing selectedItems
    return (dispatch: Function, getState: Function) => {
        const state = getState()
        const { hasDirtyTab, gridSelectedItems: currentSelectedItems, formMode, tabs } = state.pagesSlice[name]

        if (_.isEqual(selectedItems, currentSelectedItems)) {
            if (nextAction) {
                dispatch(nextAction)
            }
            return
        }

        const forms = tabs.map((t: any) => t.name.endsWith('Form') ? t.name : null).filter((t: any) => t)

        const confirmCb = () => {
            dispatch(pagesSlice.actions.selectionChanged({ name, selectedItems, selectionFilter }))
            const modes: any = {}
            if (selectedItems !== undefined && selectedItems.length && forms) {
                forms.forEach((f: string) => {
                    modes[f] = FormMode.Edit
                })
            } else {
                forms.forEach((f: string) => {
                    modes[f] = FormMode.Create
                })
            }
            dispatch(setFormModes({ name, modes }))
            dispatch(pagesSlice.actions.resetDirty({ name }))

            if (nextAction) {
                dispatch(nextAction)
            }
        }

        const rejectCb = () => {
            cancelAction && dispatch(cancelAction)
        }

        if (hasDirtyTab && !force) {  //  && (currentSelectedItems.length === 0 || selectedItems.length === 0 || !_.isEqual(currentSelectedItems[0], selectedItems[0]))) {
            dispatch(showCPopup({
                title: i18n.t('global:label.discardChanges'),
                description: i18n.t('global:question.discardChanges'),
                confirmCb,
                rejectCb,
            }))
        } else {
            confirmCb()
        }
    }
}

type ILoadModuleRightsArg = { name: string, moduleName: string }
export const loadModuleRights = createAsyncThunk('pagesSlice/loadModuleRights', async ({ name, moduleName }: ILoadModuleRightsArg, { rejectWithValue }) => {
    // const res = await client.get(`/api/ixweb/module-rights/?module_name=${moduleName}`)
    const res: any = {
        data: 1
    }

    if (typeof(res.data) === 'object' && res.data.hasOwnProperty('status')) {  // winrest response
        if (res.data.status === 'Failed') {
            return rejectWithValue(parseMessages(res.data.messages))
        } else {
            return {
                name,
                moduleRights: res.data.data
            }
        }
    } else {  // backend response
        return {
            name,
            moduleRights: res.data
        }
    }
})

const pagesSlice = createSlice({
    name: 'pages',
    initialState: {
        'Ix1000Page': getInitialPageState('page', 'per', []),
        'Ix1033UmlBetSubpagePage': getInitialPageState('subpage', null, []),
        'Ix1072Page': getInitialPageState('page', 'flbel', []),
        'Ix2000biPage': getInitialPageState('page', 'ix2000bi', []),
        'Ix20011Page': getInitialPageState('page', 'ix20011', []),
        'Ix202001Page': getInitialPageState('page', 'ix202001', []),
        'Ix2031Page': getInitialPageState('page', 'ix2031', []),
        'Ix2044Page': getInitialPageState('page', 'ix2044', []),
        'Ix2048Page': getInitialPageState('page', 'ix2048', []),
        'Ix2060Page': getInitialPageState('page', 'ix2060', []),
        'Ix2067Page': getInitialPageState('page', 'ix2067', []),
        'Ix2076Page': getInitialPageState('page', 'ix2076', []),
        'IxAAPage': getInitialPageState('page', 'aa', []),
        'IxAbrKartePage': getInitialPageState('page', 'abrKarte', []),
        'IxAuftragAbnahmeSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragAngebotKreditorSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragBuchungsPostenSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragHinweisPage': getInitialPageState('page', 'auftragHinweis', []),
        'IxAuftragHistorySubpage': getInitialPageState('subpage', null, []),
        'IxAuftragMassnahmeSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragMeldungSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragMerkmalSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragMultimediaSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragPage': getInitialPageState('page', 'auftrag', []),
        'IxAuftragPositionSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragPrioPage': getInitialPageState('page', 'auftragPrio', []),
        'IxAuftragRechnungSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragsArtKlassePage': getInitialPageState('page', 'auftragsArtKlasse', []),
        'IxAuftragsArtPage': getInitialPageState('page', 'auftragsArt', []),
        'IxAuftragSBriefArchivSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragSicherungSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragsStatusArtPage': getInitialPageState('page', 'auftragsStatusArt', []),
        'IxAuftragTerminModulePage': getInitialPageState('page', 'auftragTermin', []),
        'IxAuftragTerminPage': getInitialPageState('page', 'auftragTermin', []),
        'IxAuftragTerminSubpage': getInitialPageState('subpage', null, []),
        'IxAuftragVertragSubpage': getInitialPageState('subpage', null, []),
        'IxBackgroundJobs': getInitialPageState('page', 'bgJob', []),
        'IxBtkreisflstmrelSubpage': getInitialPageState('subpage', null, []),
        'IxBtKreisPage': getInitialPageState('page', 'btKreis', []),
        'IxBuDimTestPage': getInitialPageState('page', 'buDim', []),
        'IxChangeFlartPage': getInitialPageState('page', 'changeFlart', []),
        'IxContractBreakConditionSubpage': getInitialPageState('subpage', null, []),
        'IxContractFlantSubpage': getInitialPageState('subpage', null, []),
        'IxContractIndexedRentSubpage': getInitialPageState('subpage', null, []),
        'IxContractMaintenanceParticipanceSubpage': getInitialPageState('subpage', null, []),
        'IxContractOptionConditionSubpage': getInitialPageState('subpage', null, []),
        'IxContractPage': getInitialPageState('subpage', null, []),
        'IxContractRentAmountsSubpage': getInitialPageState('subpage', null, []),
        'IxContractRentFreeTimeSubpage': getInitialPageState('subpage', null, []),
        'IxContractRevenueRentSubpage': getInitialPageState('subpage', null, []),
        'IxContractsSubpage': getInitialPageState('subpage', null, []),
        'IxCopyAbrArtPage': getInitialPageState('page', 'copyAbrArt', []),
        'IxCopyFlartPage': getInitialPageState('page', 'copyFlart', []),
        'IxErlSchm200010BuPage': getInitialPageState('page', 'erlSchm200010Bu', []),
        'IxEsolldscSubpage': getInitialPageState('subpage', null, []),
        'IxFlantSubpage': getInitialPageState('subpage', null, []),
        'IxFlartPage': getInitialPageState('page', 'flart', []),
        'IxFlssoSubpage': getInitialPageState('subpage', null, []),
        'IxFlstmPage': getInitialPageState('page', 'flstm', []),
        'IxGenehmigungDefinitionHistorySubpage': getInitialPageState('subpage', null, []),
        'IxGenehmigungDefinitionPage': getInitialPageState('page', 'genehmigungDefinition', []),
        'IxGenehmigungKategorieSubpage': getInitialPageState('subpage', null, []),
        'IxGruppenDbPage': getInitialPageState('page', 'gruppenDb', []),
        'IxGwMaengelStatusPage': getInitialPageState('page', 'gwMaengelStatus', []),
        'IxGwSicherungsArtPage': getInitialPageState('page', 'gwSicherungsArt', []),
        'IxGwSicherungsStatusPage': getInitialPageState('page', 'gwSicherungsStatus', []),
        'IxIssuesPage': getInitialPageState('page', 'issue', []),
        'IxJobPage': getInitialPageState('page', 'job', []),
        'IxKtgDimTestPage': getInitialPageState('page', 'ktgDim', []),
        'IxLeistungsArtPage': getInitialPageState('page', 'leistungsArt', []),
        'IxLeistungsVerzeichnisPage': getInitialPageState('page', 'leistungsVerzeichnis', []),
        'IxLieferBedingungPage': getInitialPageState('page', 'lieferBedingung', []),
        'IxMahndatenPage': getInitialPageState('page', 'mahndaten', []),
        'IxMahnKarteiSubpage': getInitialPageState('subpage', null, []),
        'IxMahnKontoauszugSubpage': getInitialPageState('subpage', null, []),
        'IxMahnTmkSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungAngebotKreditorSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungAngebotSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungAuftragSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungHinweis3Page': getInitialPageState('page', 'meldungHinweis3', []),
        'IxMeldungHinweisPage': getInitialPageState('page', 'meldungHinweis', []),
        'IxMeldungHistorySubpage': getInitialPageState('subpage', null, []),
        'IxMeldungMerkmalSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungMultimediaSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungPage': getInitialPageState('page', 'meldung', []),
        'IxMeldungPrioPage': getInitialPageState('page', 'meldungPrio', []),
        'IxMeldungsArtKlassePage': getInitialPageState('page', 'meldungsArtKlasse', []),
        'IxMeldungsArtPage': getInitialPageState('page', 'meldungsArt', []),
        'IxMeldungSBriefArchivSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungStatusArtPage': getInitialPageState('page', 'meldungStatusArt', []),
        'IxMeldungTerminRegelSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungTerminSubpage': getInitialPageState('subpage', null, []),
        'IxMeldungVersFallGwrkPage': getInitialPageState('page', 'meldungVersFallGwrk', []),
        'IxMeldungVersFallPage': getInitialPageState('page', 'meldungVersFall', []),
        'IxMeldungVertragSubpage': getInitialPageState('subpage', null, []),
        'IxNcremTerminSubpage': getInitialPageState('subpage', null, []),
        'IxObjOwnSubpage': getInitialPageState('subpage', null, []),
        'IxObjPage': getInitialPageState('page', 'obj', []),
        'IxOwnPage': getInitialPageState('page', 'own', []),
        'IxPerRentAmountsSubpage': getInitialPageState('subpage', null, []),
        'IxPersonsSubpage': getInitialPageState('subpage', null, []),
        'IxPrnsrvcPage': getInitialPageState('page', 'ixPrnsrvc', []),
        'IxPrnSrvcPrinterSubpage': getInitialPageState('subpage', null, []),
        'IxprnsrvcprinterSubpage': getInitialPageState('subpage', null, []),
        'IxProcess1001Page': getInitialPageState('page', 'process1001', []),
        'IxProcess1003Page': getInitialPageState('page', 'process1003', []),
        'IxProcess1005Page': getInitialPageState('page', 'process1005', []),
        'IxProcess1006Page': getInitialPageState('page', 'process1006', []),
        'IxProcess101002ParamPage': getInitialPageState('page', 'process101002Param', []),
        'IxProcess101003Page': getInitialPageState('page', 'process101003', []),
        'IxProcess1010Page': getInitialPageState('page', 'process1010', []),
        'IxProcess102001Page': getInitialPageState('page', 'process102001', []),
        'IxProcess102002Page': getInitialPageState('page', 'process102002', []),
        'IxProcess102003Page': getInitialPageState('page', 'process102003', []),
        'IxProcess1021ParamPage': getInitialPageState('page', 'process1021Param', []),
        'IxProcess1022BuSperrePage': getInitialPageState('page', 'process1022BuSperre', []),
        'IxProcess1022KtoBuSperreAutoPage': getInitialPageState('page', 'process1022KtoBuSperreAuto', []),
        'IxProcess1022KtoDelPage': getInitialPageState('page', 'process1022KtoDel', []),
        'IxProcess1022KtoDupPage': getInitialPageState('page', 'process1022KtoDup', []),
        'IxProcess1022KtoDupRefPage': getInitialPageState('page', 'process1022KtoDupRef', []),
        'IxProcess1022KtoRenamePage': getInitialPageState('page', 'process1022KtoRename', []),
        'IxProcess103002Page': getInitialPageState('page', 'process103002', []),
        'IxProcess1033AnteileVorperiodeUebernehmenPage': getInitialPageState('page', 'process1033AnteileVorperiodeUebernehmen', []),
        'IxProcess1033CopyAnteilePage': getInitialPageState('page', 'process1033CopyAnteile', []),
        'IxProcess1033CopyUmlPage': getInitialPageState('page', 'process1033CopyUml', []),
        'IxProcess1033Page': getInitialPageState('page', 'process1033', []),
        'IxProcess1033RegGesAnteilePage': getInitialPageState('page', 'process1033RegGesAnteile', []),
        'IxProcess1033RenameUmlPage': getInitialPageState('page', 'process1033RenameUml', []),
        'IxProcess1040Page': getInitialPageState('page', 'process1040', []),
        'IxProcess1061Page': getInitialPageState('page', 'process1061', []),
        'IxProcess107049Page': getInitialPageState('page', 'process107049', []),
        'IxProcess107050Page': getInitialPageState('page', 'process107050', []),
        'IxProcess107055Page': getInitialPageState('page', 'process107055', []),
        'IxProcess107101Page': getInitialPageState('page', 'process107101', []),
        'IxProcess107102Page': getInitialPageState('page', 'process107102', []),
        'IxProcess107103Page': getInitialPageState('page', 'process107103', []),
        'IxProcess1071041Page': getInitialPageState('page', 'process1071041', []),
        'IxProcess107104Page': getInitialPageState('page', 'process107104', []),
        'IxProcess107105Page': getInitialPageState('page', 'process107105', []),
        'IxProcess107108Page': getInitialPageState('page', 'process107108', []),
        'IxProcess107109Page': getInitialPageState('page', 'process107109', []),
        'IxProcess107110Page': getInitialPageState('page', 'process107110', []),
        'IxProcess107113Page': getInitialPageState('page', 'process107113', []),
        'IxProcess107115Page': getInitialPageState('page', 'process107115', []),
        'IxProcess107120Page': getInitialPageState('page', 'process107120', []),
        'IxProcess107122Page': getInitialPageState('page', 'process107122', []),
        'IxProcess107123Page': getInitialPageState('page', 'process107123', []),
        'IxProcess107128Page': getInitialPageState('page', 'process107128', []),
        'IxProcess107129Page': getInitialPageState('page', 'process107129', []),
        'IxProcess107135Page': getInitialPageState('page', 'process107135', []),
        'IxProcess107211Page': getInitialPageState('page', 'process107211', []),
        'IxProcess107232Page': getInitialPageState('page', 'process107232', []),
        'IxProcess1072ParamPage': getInitialPageState('page', 'process1072Param', []),
        'IxProcess1072ZpModulPage': getInitialPageState('page', 'process1072ZpModul', []),
        'IxProcess1082Page': getInitialPageState('page', 'process1082', []),
        'IxProcess1084Page': getInitialPageState('page', 'process1084', []),
        'IxProcess1086Page': getInitialPageState('page', 'process1086', []),
        'IxProcess109041Page': getInitialPageState('page', 'process109041', []),
        'IxProcess1090Page': getInitialPageState('page', 'process1090', []),
        'IxProcess11722Page': getInitialPageState('page', 'process11722', []),
        'IxProcess11725Page': getInitialPageState('page', 'process11725', []),
        'IxProcess1230051Page': getInitialPageState('page', 'process1230051', []),
        'IxProcess1230053Page': getInitialPageState('page', 'process1230053', []),
        'IxProcess123010Page': getInitialPageState('page', 'process123010', []),
        'IxProcess129301Page': getInitialPageState('page', 'process129301', []),
        'IxProcess1300LetztenRestantAendernPage': getInitialPageState('page', 'process1300LetztenRestantAendern', []),
        'IxProcess1300Page': getInitialPageState('page', 'process1300', []),
        'IxProcess1300PerNrChangePage': getInitialPageState('page', 'process1300PerNrChange', []),
        'IxProcess1300VertragsbeginnPage': getInitialPageState('page', 'process1300Vertragsbeginn', []),
        'IxProcess1300VertragsendePage': getInitialPageState('page', 'process1300Vertragsende', []),
        'IxProcess1300VertragswechselPage': getInitialPageState('page', 'process1300Vertragswechsel', []),
        'IxProcess1302Page': getInitialPageState('page', 'process1302', []),
        'IxProcess1302PersonLoeschenPage': getInitialPageState('page', 'process1302PersonLoeschen', []),
        'IxProcess1302PersontypAendernPage': getInitialPageState('page', 'process1302PersontypAendern', []),
        'IxProcess1302Storno1RestantPage': getInitialPageState('page', 'process1302Storno1Restant', []),
        'IxProcess200010Page': getInitialPageState('page', 'process200010', []),
        'IxProcess200101Page': getInitialPageState('page', 'process200101', []),
        'IxProcess200105Page': getInitialPageState('page', 'process200105', []),
        'IxProcess200109Page': getInitialPageState('page', 'process200109', []),
        'IxProcess200120Page': getInitialPageState('page', 'process200120', []),
        'IxProcess200120SuSaSkA4Page': getInitialPageState('page', 'process200120SuSaSkA4', []),
        'IxProcess200120SuSaSkAbschlussPage': getInitialPageState('page', 'process200120SuSaSkAbschluss', []),
        'IxProcess200120SuSaSkSoBuPeriPage': getInitialPageState('page', 'process200120SuSaSkSoBuPeri', []),
        'IxProcess200140Page': getInitialPageState('page', 'process200140', []),
        'IxProcess2003Page': getInitialPageState('page', 'process2003', []),
        'IxProcess201004Page': getInitialPageState('page', 'process201004', []),
        'IxProcess201052Page': getInitialPageState('page', 'process201052', []),
        'IxProcess2011Page': getInitialPageState('page', 'process2011', []),
        'IxProcess2013Page': getInitialPageState('page', 'process2013', []),
        'IxProcess2014Page': getInitialPageState('page', 'process2014', []),
        'IxProcess2016Page': getInitialPageState('page', 'process2016', []),
        'IxProcess202001Page': getInitialPageState('page', 'process202001', []),
        'IxProcess2025Page': getInitialPageState('page', 'process2025', []),
        'IxProcess2031Page': getInitialPageState('page', 'process2031', []),
        'IxProcess2035ParamPage': getInitialPageState('page', 'process2035Param', []),
        'IxProcess204005Page': getInitialPageState('page', 'process204005', []),
        'IxProcess204006Page': getInitialPageState('page', 'process204006', []),
        'IxProcess20401Page': getInitialPageState('page', 'process20401', []),
        'IxProcess204031Page': getInitialPageState('page', 'process204031', []),
        'IxProcess204402Page': getInitialPageState('page', 'process204402', []),
        'IxProcess2044Page': getInitialPageState('page', 'process2044', []),
        'IxProcess2049ParamPage': getInitialPageState('page', 'process2049Param', []),
        'IxProcess2051Page': getInitialPageState('page', 'process2051', []),
        'IxProcess2052Page': getInitialPageState('page', 'process2052', []),
        'IxProcess2053Page': getInitialPageState('page', 'process2053', []),
        'IxProcess2061Page': getInitialPageState('page', 'process2061', []),
        'IxProcess206201Page': getInitialPageState('page', 'process206201', []),
        'IxProcess2063Page': getInitialPageState('page', 'process2063', []),
        'IxProcess206701ParamPage': getInitialPageState('page', 'process206701Param', []),
        'IxProcess206702ParamPage': getInitialPageState('page', 'process206702Param', []),
        'IxProcess2072Page': getInitialPageState('page', 'process2072', []),
        'IxProcess2073ParamPage': getInitialPageState('page', 'process2073Param', []),
        'IxProcess2074Page': getInitialPageState('page', 'process2074', []),
        'IxProcess2075Page': getInitialPageState('page', 'process2075', []),
        'IxProcess2076Page': getInitialPageState('page', 'process2076', []),
        'IxProcess211101Page': getInitialPageState('page', 'process211101', []),
        'IxProcess2113Page': getInitialPageState('page', 'process2113', []),
        'IxProcess2123Page': getInitialPageState('page', 'process2123', []),
        'IxProcess2124Page': getInitialPageState('page', 'process2124', []),
        'IxProcess22001Page': getInitialPageState('page', 'process22001', []),
        'IxProcess22002Page': getInitialPageState('page', 'process22002', []),
        'IxProcess2300ParamPage': getInitialPageState('page', 'process2300Param', []),
        'IxProcess240001Page': getInitialPageState('page', 'process240001', []),
        'IxProcess240001ProDruckVglPage': getInitialPageState('page', 'process240001ProDruckVgl', []),
        'IxProcess240007ParamPage': getInitialPageState('page', 'process240007Param', []),
        'IxProcess3010ParamPage': getInitialPageState('page', 'process3010Param', []),
        'IxProcess3025Page': getInitialPageState('page', 'process3025', []),
        'IxProcess3026Page': getInitialPageState('page', 'process3026', []),
        'IxProcess304002Page': getInitialPageState('page', 'process304002', []),
        'IxProcess304003Page': getInitialPageState('page', 'process304003', []),
        'IxProcess304004Page': getInitialPageState('page', 'process304004', []),
        'IxProcess304005Page': getInitialPageState('page', 'process304005', []),
        'IxProcess304006Page': getInitialPageState('page', 'process304006', []),
        'IxProcess304007Page': getInitialPageState('page', 'process304007', []),
        'IxProcess304011Page': getInitialPageState('page', 'process304011', []),
        'IxProcess304012Page': getInitialPageState('page', 'process304012', []),
        'IxProcess3057DruckPage': getInitialPageState('page', 'process3057Druck', []),
        'IxProcess3057KontrollePage': getInitialPageState('page', 'process3057Kontrolle', []),
        'IxProcess3057Page': getInitialPageState('page', 'process3057', []),
        'IxProcess3057SaveParamPage': getInitialPageState('page', 'process3057SaveParam', []),
        'IxProcess3057VzEintragenPage': getInitialPageState('page', 'process3057VzEintragen', []),
        'IxProcess404002Page': getInitialPageState('page', 'process404002', []),
        'IxProcess404011Page': getInitialPageState('page', 'process404011', []),
        'IxProcess404050ParamPage': getInitialPageState('page', 'process404050Param', []),
        'IxProcess404051ParamPage': getInitialPageState('page', 'process404051Param', []),
        'IxProcess404052ParamPage': getInitialPageState('page', 'process404052Param', []),
        'IxProcess404052ZpModulPage': getInitialPageState('page', 'process404052ZpModul', []),
        'IxProcess404053ParamPage': getInitialPageState('page', 'process404053Param', []),
        'IxProcess40600Page': getInitialPageState('page', 'process40600', []),
        'IxProcess40604Page': getInitialPageState('page', 'process40604', []),
        'IxProcess40606Page': getInitialPageState('page', 'process40606', []),
        'IxProcess6020BerechnungPage': getInitialPageState('page', 'process6020Berechnung', []),
        'IxProcess6020BuchenPage': getInitialPageState('page', 'process6020Buchen', []),
        'IxProcess6020ListePage': getInitialPageState('page', 'process6020Liste', []),
        'IxProcess6020Page': getInitialPageState('page', 'process6020', []),
        'IxProcessBisonBoxExportPage': getInitialPageState('page', 'processBisonBoxExport', []),
        'IxProcessDatevParamPage': getInitialPageState('page', 'processDatevParam', []),
        'IxProcessDmsBelegausgabeParamInterfacePage': getInitialPageState('page', 'processDmsBelegausgabeParamInterface', []),
        'IxProcessHypothekReportParamPage': getInitialPageState('page', 'processHypothekReportParam', []),
        'IxProcessIndexPage': getInitialPageState('page', 'processIndex', []),
        'IxProcessIntrealParamPage': getInitialPageState('page', 'processIntrealParam', []),
        'IxProcessIxDiamantBuExportPage': getInitialPageState('page', 'processIxDiamantBuExport', []),
        'IxProcessIxHansaInvestBuExportPage': getInitialPageState('page', 'processIxHansaInvestBuExport', []),
        'IxProcessIxHansaInvestStdBuExportPage': getInitialPageState('page', 'processIxHansaInvestStdBuExport', []),
        'IxProcessIxVblKgvBuExportPage': getInitialPageState('page', 'processIxVblKgvBuExport', []),
        'IxProcessKe311271Page': getInitialPageState('page', 'processKe311271', []),
        'IxProcessKx0224SapBuExportPage': getInitialPageState('page', 'processKx0224SapBuExport', []),
        'IxProcessKx10015BuExportPage': getInitialPageState('page', 'processKx10015BuExport', []),
        'IxProcessKx3124qexParamPage': getInitialPageState('page', 'processKx3124qexParam', []),
        'IxProcessKxBnpSapBuExportPage': getInitialPageState('page', 'processKxBnpSapBuExport', []),
        'IxProcessKxBuUserInfoExportPage': getInitialPageState('page', 'processKxBuUserInfoExport', []),
        'IxProcessKxDiamantAlsterdorfBuExpPage': getInitialPageState('page', 'processKxDiamantAlsterdorfBuExp', []),
        'IxProcessKxDiamantCureusBuExportPage': getInitialPageState('page', 'processKxDiamantCureusBuExport', []),
        'IxProcessKxDiamantMLImmoBuExportPage': getInitialPageState('page', 'processKxDiamantMLImmoBuExport', []),
        'IxProcessKxLvmSapBuExportPage': getInitialPageState('page', 'processKxLvmSapBuExport', []),
        'IxProcessPrinterSetupParamInterfacePage': getInitialPageState('page', 'processPrinterSetupParamInterface', []),
        'IxProcessr201051Page': getInitialPageState('subpage', null, []),
        'IxProcessr201052Page': getInitialPageState('page', 'processr201052', []),
        'IxProcessr2072Page': getInitialPageState('page', 'processr2072', []),
        'IxProcessrIndexPage': getInitialPageState('page', 'processrIndex', []),
        'IxProcessSaveAutoZustellungParamPage': getInitialPageState('page', 'processSaveAutoZustellungParam', []),
        'IxProcessStdBuExportPage': getInitialPageState('page', 'processStdBuExport', []),
        'IxProcessTrassetsExportPage': getInitialPageState('page', 'processTrassetsExport', []),
        'IxProcessTrinityExportPage': getInitialPageState('page', 'processTrinityExport', []),
        'IxProcessVblExportParamPage': getInitialPageState('page', 'processVblExportParam', []),
        'IxReport1022Page': getInitialPageState('page', 'report1022', []),
        'IxReport107106Page': getInitialPageState('page', 'report107106', []),
        'IxReport1071Page': getInitialPageState('page', 'report1071', []),
        'IxReport200084Page': getInitialPageState('page', 'report200084', []),
        'IxReport200085Page': getInitialPageState('page', 'report200085', []),
        'IxReport201051Page': getInitialPageState('page', 'report201051', []),
        'IxReport2010Page': getInitialPageState('page', 'report2010', []),
        'IxReport206202VerprobungPage': getInitialPageState('page', 'report206202Verprobung', []),
        'IxReport206202VSkAbstimmungPage': getInitialPageState('page', 'report206202VSkAbstimmung', []),
        'IxReport2400KtoAuszugPage': getInitialPageState('page', 'report2400KtoAuszug', []),
        'IxReportParERechnungReadyPage': getInitialPageState('page', 'reportParERechnungReady', []),
        'IxSollstellungPage': getInitialPageState('page', 'sollstellung', []),
        'IxSollstellungSimulationPage': getInitialPageState('page', 'sollstellungSimulation', []),
        'IxTOAuftragSubpage': getInitialPageState('subpage', null, []),
        'IxTODefPage': getInitialPageState('page', 'tODef', []),
        'IxTODefTOHistorySubpage': getInitialPageState('subpage', null, []),
        'IxTODefTOMerkmalDefSubpage': getInitialPageState('subpage', null, []),
        'IxTOGesundheitsamtPage': getInitialPageState('page', 'toGesundheitsamt', []),
        'IxTOHerstellerPage': getInitialPageState('page', 'toHersteller', []),
        'IxTOHistorySubpage': getInitialPageState('subpage', null, []),
        'IxTOMeldungSubpage': getInitialPageState('subpage', null, []),
        'IxTOMerkmalSubpage': getInitialPageState('subpage', null, []),
        'IxTOMultimediaSubpage': getInitialPageState('subpage', null, []),
        'IxTOPage': getInitialPageState('page', 'to', []),
        'IxTOPruefungsartPage': getInitialPageState('page', 'tOPruefungsart', []),
        'IxTOPruefungsartTOHistorySubpage': getInitialPageState('subpage', null, []),
        'IxTOPruefZyklusSubpage': getInitialPageState('subpage', null, []),
        'IxTORechtsgrundlagenPage': getInitialPageState('page', 'tORechtsgrundlagen', []),
        'IxToSbriefArchivSubpage': getInitialPageState('subpage', null, []),
        'IxTOSchluesselAusgabeSubpage': getInitialPageState('subpage', null, []),
        'IxToTerminSubpage': getInitialPageState('subpage', null, []),
        'IxTOVertragSubpage': getInitialPageState('subpage', null, []),
        'IxTOWartungsHistorySubpage': getInitialPageState('subpage', null, []),
        'IxTOWartZyklusSubpage': getInitialPageState('subpage', null, []),
        'IxUmlPage': getInitialPageState('page', 'uml', []),
        'IxVertragSubpage': getInitialPageState('subpage', null, []),
        'IxVtgkschutzSubpage': getInitialPageState('subpage', null, []),
        'IxXspgetzlgtlrzPage': getInitialPageState('page', 'xspgetzlgtlrz', []),
        'IxZahlungsBedingungPage': getInitialPageState('page', 'zahlungsBedingung', []),
    } as any,
    reducers: {
        // add and remove pages from slice
        setPageData: (state, action) => {
            const { name, tabs, parentPageName = null, tableNamesMap = {} } = action.payload
            state[name].tabs = tabs
            const tabProps: any = {}
            tabs.forEach(({ name: tabName }: any) => tabProps[tabName] = {})
            state[name].tabProps = tabProps
            state[name].parentPageName = parentPageName
            state[name].tableNamesMap = tableNamesMap
        },
        closePage: (state, action) => {
            const { name } = action.payload
            delete state[name]
        },
        switchTabIndex: (state, action) => {
            const { name, tabIndex } = action.payload
            state[name].activeTabIndex = tabIndex

            if (tabIndex > 0 && state[name].gridSelectedItems && state[name].gridSelectedItems.length && !_.isEqual(state[name].selectedItem, state[name].gridSelectedItems[0])) {
                state[name].selectedItem = state[name].gridSelectedItems[0]
            }
        },
        switchTab: (state, action) => {
            const { name, tabName } = action.payload
            const tabIndex = state[name].tabs.findIndex((x: any) => x.name === tabName)

            state[name].activeTabIndex = tabIndex
            if (tabIndex > 0 && state[name].gridSelectedItems && state[name].gridSelectedItems.length && !_.isEqual(state[name].selectedItem, state[name].gridSelectedItems[0])) {
                state[name].selectedItem = state[name].gridSelectedItems[0]
            }
        },
        backToFirstTab: (state, action) => {
            const { name } = action.payload
            state[name].activeTabIndex = 0
        },

        setTabProps: (state, action) => {
            const { name, tabName, props } = action.payload
            state[name].tabProps[tabName] = props
        },

        // manage forms state
        setDirty: (state, action) => {
            const { name, formName, dirty } = action.payload
            state[name].dirty[formName] = dirty
            state[name].hasDirtyTab = Boolean(Object.values(state[name].dirty).find((d: any) => d === true))
            state[name].hasDirtyTabMain = Boolean(Object.entries(state[name].dirty).find(([k, v]) => !k.endsWith('Subpage') && v))
        },
        resetDirty: (state, action) => {
            const { name } = action.payload
            state[name].dirty = {}
            state[name].hasDirtyTab = false
            state[name].hasDirtyTabMain = false
        },

        selectionChanged: (state, action) => {
            const { name, selectedItems, selectionFilter, changeBanner } = action.payload
            state[name].gridSelectedItems = selectedItems
            state[name].selectionFilter = selectionFilter || []
            if (changeBanner !== false) {
                state[name].selectionBanner = selectedItems && selectedItems.length ? selectedItems[0] : null
            }
        },

        setSelectedItem: (state, action) => {
            const { name, selectedItem } = action.payload
            state[name].selectedItem = selectedItem
        },

        selectionBannerChanged: (state, action) => {
            const { name, banner } = action.payload
            state[name].selectionBanner = banner
        },

        selectionBannerLookupDataChanged: (state, action) => {
            const { name, bannerLookupData } = action.payload

            const newData = { ...state[name].selectionBannerLookupData, ...bannerLookupData }
            state[name].selectionBannerLookupData = newData
        },

        setFormModes: (state, action) => {
            const { name, modes } = action.payload
            state[name].formMode = { ...state[name].formMode, ...modes }
        },

        resetPage: (state, action) => {
            const { name } = action.payload
            state[name].activeTabIndex = 0
            state[name].formMode = {}
            state[name].dirty = {}
            state[name].hasDirtyTab = false
            state[name].hasDirtyTabMain = false
            state[name].gridSelectedItems = []
            state[name].selectedItem = null
            state[name].selectionBanner = null
            state[name].selection = Selection.NoSelection
        },
        openPopup: (state, action) => {
            const { name, popupName, data } = action.payload
            state[name].popups[popupName] = true
            state[name].popupData = data
            if (state[name].gridSelectedItems && state[name].gridSelectedItems.length && !_.isEqual(state[name].selectedItem, state[name].gridSelectedItems[0])) {
                state[name].selectedItem = state[name].gridSelectedItems[0]
            }
        },
        closePopup: (state, action) => {
            const { name, popupName } = action.payload
            state[name].popups[popupName] = false
        },
        openDynamicPopup: (state, action) => {
            const { name, component, data, title } = action.payload
            state[name].dynamicPopup = component
            state[name].popupData = data
            state[name].popupTitle = title
        },
        closeDynamicPopup: (state, action) => {
            const { name } = action.payload
            state[name].dynamicPopup = null
            state[name].popupData = null
            state[name].popupTitle = ''
        },
        setSelection: (state, action) => {
            const { name, selection } = action.payload
            state[name].selection = selection
        },
        setGridFilters: (state, action) => {
            const { name, filterValues, filterLookupValues } = action.payload

            if (!_.isEqual(state[name].gridFilters, { filterValues, filterLookupValues })) {
                state[name].gridFilters = { filterValues, filterLookupValues }
            }
        },
        updateReloadedItem: (state, action) => {
            const { name, item } = action.payload
            const ind = state[name].gridSelectedItems.findIndex((i: any) => i.HDR_ID === item.HDR_ID)
            ind >= 0 && (state[name].gridSelectedItems[ind] = item)
            state[name].selectedItem = item
        },

        setItemsToReload: (state, action) => {
            const { name, itemsToReload } = action.payload
            state[name].itemsToReload = itemsToReload
        },
        setItemsToRemove: (state, action) => {
            const { name, itemsToRemove } = action.payload
            state[name].itemsToRemove = itemsToRemove
        },
        setMultiformPart: (state, action) => {
            const { name, multiformName, values } = action.payload
            if (!name || !multiformName) { return }

            state[name].multiforms[multiformName] = { ...state[name].multiforms[multiformName], ...values }
        }
    },
    extraReducers: builder => {
        builder.addCase(loadModuleRights.fulfilled, (state, action) => {
            const { name, moduleRights } = action.payload
            state[name].moduleRights = moduleRights
        })
        builder.addCase(loadModuleRights.rejected, (_, action) => {
            if (action.error.message !== 'Rejected') {
                notify(action.error.message)
            } else {
                notify(action.payload)
            }
        })
    }
})

export const {
    setPageData, closePage, setTabProps, switchTabIndex, backToFirstTab, setDirty,
    setFormModes, selectionBannerChanged, resetPage, resetDirty: _resetDirty,
    selectionChanged: _selectionChanged, openPopup, closePopup, setSelectedItem, setSelection,
    switchTab: _switchTab, selectionBannerLookupDataChanged, setGridFilters,
    openDynamicPopup, closeDynamicPopup, updateReloadedItem, setItemsToReload,
    setItemsToRemove, setMultiformPart,
} = pagesSlice.actions
export default pagesSlice.reducer
